import {
  ClientMessage,
  GAME_CONSTANTS,
  PROTOCOL_VERSION,
  ServerMessage,
  TankInput,
} from '@tankworld/shared';
import { useGameStore } from '../state/game-store.js';

export class NetworkClient {
  private socket: WebSocket | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  public connect(roomId?: string): Promise<boolean> {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve(true);
    }

    this.isConnecting = true;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use host and port from window.location in browser
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    return new Promise((resolve) => {
      try {
        console.log(`[NetworkClient] Connecting to WebSocket: ${wsUrl}`);
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          console.log('[NetworkClient] WebSocket connected.');
          this.isConnecting = false;
          this.reconnectAttempts = 0;

          // Send join_room
          const store = useGameStore.getState();
          this.send({
            version: PROTOCOL_VERSION,
            type: 'join_room',
            roomId,
            playerName: store.playerName,
            color: '#4b5320',
          });

          this.startPingLoop();
          resolve(true);
        };

        this.socket.onmessage = (event) => {
          this.handleServerMessage(event.data);
        };

        this.socket.onerror = (err) => {
          console.warn('[NetworkClient] WebSocket error:', err);
          resolve(false);
        };

        this.socket.onclose = (event) => {
          console.log(`[NetworkClient] WebSocket closed (code: ${event.code})`);
          this.stopPingLoop();
          this.socket = null;
        };
      } catch (err) {
        console.error('[NetworkClient] Exception creating WebSocket:', err);
        resolve(false);
      }
    });
  }

  private handleServerMessage(raw: string): void {
    try {
      const msg: ServerMessage = JSON.parse(raw);
      const store = useGameStore.getState();

      switch (msg.type) {
        case 'player_joined':
          if (msg.isSelf) {
            store.setLocalPlayerId(msg.playerId);
            store.setActiveRoom(msg.room);
          }
          break;

        case 'room_state_update':
          store.setActiveRoom(msg.room);
          break;

        case 'countdown_tick':
          store.setCountdown(msg.secondsRemaining);
          break;

        case 'chat_broadcast':
          store.addChatMessage(msg.chat);
          break;

        case 'match_started':
          store.setGameMode('multiplayer');
          break;

        case 'state_snapshot':
          store.updateWorldSnapshot(msg.tanks, msg.projectiles, msg.events, msg.tick);
          break;

        case 'pong': {
          const rtt = Math.max(1, Date.now() - msg.clientTimestamp);
          store.updateDiagnostics(store.fps, rtt, store.serverTick);
          break;
        }

        case 'match_ended':
          console.log('[NetworkClient] Match ended:', msg.leaderboard);
          break;

        case 'server_error':
          console.error(`[NetworkClient] Server error [${msg.code}]: ${msg.message}`);
          break;
      }
    } catch (err) {
      console.warn('[NetworkClient] Failed to parse server message:', err);
    }
  }

  public send(msg: ClientMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(msg));
    }
  }

  public setReady(isReady: boolean): void {
    this.send({ type: 'set_ready', isReady });
  }

  public setTeam(team: 'red' | 'blue' | 'none'): void {
    this.send({ type: 'set_team', team });
  }

  public selectTank(tankClassId: string): void {
    this.send({ type: 'select_tank', tankClassId });
  }

  public sendChat(message: string): void {
    this.send({ type: 'send_chat', message });
  }

  public startMatch(): void {
    this.send({ type: 'start_match' });
  }

  public updateRoomSettings(settings: { mapId?: string; mode?: any; botCount?: number }): void {
    this.send({ type: 'update_room_settings', ...settings });
  }

  public sendInput(input: TankInput): void {
    this.send({
      type: 'player_input',
      sequence: input.sequence,
      throttle: input.throttle,
      steer: input.steer,
      brake: input.brake,
      turretYaw: input.turretYaw,
      gunPitch: input.gunPitch,
    });
  }

  public sendFireRequest(sequence: number): void {
    this.send({
      type: 'fire_request',
      sequence,
    });
  }

  private startPingLoop(): void {
    this.stopPingLoop();
    this.pingInterval = setInterval(() => {
      this.send({
        type: 'ping',
        clientTimestamp: Date.now(),
      });
    }, GAME_CONSTANTS.PING_INTERVAL_MS);
  }

  private stopPingLoop(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  public disconnect(): void {
    this.stopPingLoop();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
}

export const networkClient = new NetworkClient();
